import React, { useState } from 'react';
import { 
  Card, Button, Space, Table, message, Row, Col, 
  Input, Select, Modal, Form, Tag, Alert, Tooltip, Spin, InputNumber
} from 'antd';
import { 
  CopyOutlined, WalletOutlined, SearchOutlined,
  DeleteOutlined, LinkOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';

// 接口定义
interface NFTHoldingEntry {
  id: number;
  ownerAddress: string;
  balance: string;
  status: 'idle' | 'loading' | 'success' | 'error';
  isSelected: boolean;
}

interface ConfigState {
  rpcUrl: string;
  nftContractAddress: string;
  nftTokenId: string;
}

interface ContractInfo {
  detectedStandard: 'Idle' | 'Checking' | 'ERC721' | 'ERC1155' | 'Unknown';
  name: string;
  symbol: string;
}

interface QueryProgress {
  checked: number;
  total: number;
}

// 预设网络配置
const NETWORK_CONFIGS = [
  {
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH',
    explorer: 'https://etherscan.io/address/'
  },
  {
    name: 'BNB Smart Chain',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB',
    explorer: 'https://bscscan.com/address/'
  },
  {
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC',
    explorer: 'https://polygonscan.com/address/'
  },
  {
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH',
    explorer: 'https://arbiscan.io/address/'
  },
  {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH',
    explorer: 'https://basescan.org/address/'
  },
];

// NFT ABI (包含ERC165、ERC721、ERC1155的必要函数)
const NFT_ABI = [
  // ERC165
  "function supportsInterface(bytes4 interfaceId) view returns (bool)",
  // ERC721
  "function balanceOf(address owner) view returns (uint256)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  // ERC1155
  "function balanceOf(address account, uint256 id) view returns (uint256)"
];

const NftChecker: React.FC = () => {
  // 状态管理
  const [nftHoldings, setNftHoldings] = useState<NFTHoldingEntry[]>([]);
  const [config, setConfig] = useState<ConfigState>({
    rpcUrl: NETWORK_CONFIGS[0].rpcUrl,
    nftContractAddress: '',
    nftTokenId: '0'
  });
  const [contractInfo, setContractInfo] = useState<ContractInfo>({
    detectedStandard: 'Idle',
    name: '',
    symbol: ''
  });
  const [isQuerying, setIsQuerying] = useState(false);
  const [queryProgress, setQueryProgress] = useState<QueryProgress>({ checked: 0, total: 0 });
  const [totalHoldings, setTotalHoldings] = useState<string>('0');
  const [currentExplorer, setCurrentExplorer] = useState(NETWORK_CONFIGS[0].explorer);
  
  // Modal 状态
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [addressesInput, setAddressesInput] = useState('');

  // 样式定义
  const buttonStyle = {
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '14px',
    height: '36px',
    minWidth: '80px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
  };

  const componentStyle = {
    borderRadius: '6px',
    fontSize: '14px'
  };

  // 工具函数
  const isValidAddress = (address: string): boolean => {
    return ethers.isAddress(address);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label}已复制到剪贴板`);
  };

  // 监听网络变化，更新浏览器
  React.useEffect(() => {
    const network = NETWORK_CONFIGS.find(n => n.rpcUrl === config.rpcUrl);
    if (network) {
      setCurrentExplorer(network.explorer);
    }
  }, [config.rpcUrl]);

  // 导入钱包地址
  const handleImportWallets = () => {
    if (!addressesInput.trim()) {
      message.error('请输入钱包地址');
      return;
    }

    const addresses = addressesInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const validAddresses: string[] = [];
    const invalidAddresses: string[] = [];

    addresses.forEach((address, index) => {
      if (isValidAddress(address)) {
        validAddresses.push(address);
      } else {
        invalidAddresses.push(`第${index + 1}行`);
      }
    });

    if (invalidAddresses.length > 0) {
      message.error(`发现${invalidAddresses.length}个无效地址: ${invalidAddresses.join(', ')}`);
      return;
    }

    if (validAddresses.length === 0) {
      message.error('没有有效的地址');
      return;
    }

    // 去重处理
    const existingAddresses = new Set(nftHoldings.map(h => h.ownerAddress.toLowerCase()));
    const newAddresses = validAddresses.filter(addr => 
      !existingAddresses.has(addr.toLowerCase())
    );

    if (newAddresses.length === 0) {
      message.warning('所有地址都已存在');
      return;
    }

    const newHoldings: NFTHoldingEntry[] = newAddresses.map((address, index) => ({
      id: Date.now() + index,
      ownerAddress: address,
      balance: '0',
      status: 'idle',
      isSelected: false
    }));

    setNftHoldings(prev => [...prev, ...newHoldings]);
    setIsImportModalVisible(false);
    setAddressesInput('');
    message.success(`成功导入${newHoldings.length}个地址`);
  };

  // 删除选中的地址
  const handleDeleteSelected = () => {
    const selectedCount = nftHoldings.filter(h => h.isSelected).length;
    if (selectedCount === 0) {
      message.warning('没有选中的地址');
      return;
    }

    setNftHoldings(prev => prev.filter(holding => !holding.isSelected));
    message.success(`已删除${selectedCount}个地址`);
  };

  // 合约标准检测
  const handleDetectStandard = async () => {
    const contractAddress = config.nftContractAddress.trim();

    if (!contractAddress) {
      setContractInfo({
        detectedStandard: 'Idle',
        name: '',
        symbol: ''
      });
      return;
    }

    if (!isValidAddress(contractAddress)) {
      message.error('请输入有效的合约地址');
      return;
    }

    setContractInfo(prev => ({ ...prev, detectedStandard: 'Checking' }));

    try {
      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const contract = new ethers.Contract(contractAddress, NFT_ABI, provider);

      // 并发检测ERC721和ERC1155接口
      const [supportsERC721, supportsERC1155, name, symbol] = await Promise.all([
        contract.supportsInterface('0x80ac58cd'), // ERC721接口ID
        contract.supportsInterface('0xd9b67a26'), // ERC1155接口ID
        contract.name().catch(() => ''),
        contract.symbol().catch(() => '')
      ]);

      let detectedStandard: ContractInfo['detectedStandard'];
      if (supportsERC1155) {
        detectedStandard = 'ERC1155';
      } else if (supportsERC721) {
        detectedStandard = 'ERC721';
      } else {
        detectedStandard = 'Unknown';
      }

      setContractInfo({
        detectedStandard,
        name,
        symbol
      });

      if (detectedStandard === 'Unknown') {
        message.warning('未能识别NFT标准，请确认合约地址正确');
      } else {
        message.success(`检测成功: ${detectedStandard} ${name ? `(${name})` : ''}`);
      }

    } catch (error) {
      console.error('合约标准检测失败:', error);
      setContractInfo({
        detectedStandard: 'Unknown',
        name: '',
        symbol: ''
      });
      message.error('合约标准检测失败，请检查合约地址和网络连接');
    }
  };

  // 批处理工具函数
  const processInBatches = async <T,>(
    items: T[],
    batchSize: number,
    processFn: (item: T) => Promise<void>
  ): Promise<void> => {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await Promise.all(batch.map(item => processFn(item)));
    }
  };

  // 单个NFT持有量查询函数
  const fetchHoldingForEntry = async (entry: NFTHoldingEntry): Promise<void> => {
    try {
      // 更新状态为加载中
      setNftHoldings(prev => prev.map(h =>
        h.id === entry.id ? { ...h, status: 'loading' } : h
      ));

      const provider = new ethers.JsonRpcProvider(config.rpcUrl);
      const contract = new ethers.Contract(config.nftContractAddress, NFT_ABI, provider);

      let balance: bigint;

      // 根据标准选择调用方法
      if (contractInfo.detectedStandard === 'ERC721') {
        balance = await contract.balanceOf(entry.ownerAddress);
      } else if (contractInfo.detectedStandard === 'ERC1155') {
        balance = await contract.balanceOf(entry.ownerAddress, config.nftTokenId);
      } else {
        throw new Error('未知的NFT标准');
      }

      const balanceString = balance.toString();

      // 更新持有量和状态
      setNftHoldings(prev => prev.map(h =>
        h.id === entry.id ? {
          ...h,
          balance: balanceString,
          status: 'success'
        } : h
      ));

      // 累加总持有量
      setTotalHoldings(prev => {
        const prevTotal = BigInt(prev);
        const newTotal = prevTotal + balance;
        return newTotal.toString();
      });

    } catch (error) {
      console.error(`查询NFT持有量失败 ${entry.ownerAddress}:`, error);

      // 更新状态为错误
      setNftHoldings(prev => prev.map(h =>
        h.id === entry.id ? { ...h, status: 'error', balance: '0' } : h
      ));
    } finally {
      // 更新进度
      setQueryProgress(prev => ({ ...prev, checked: prev.checked + 1 }));
    }
  };

  // 核心查询持有量功能
  const handleQueryHoldings = async () => {
    // 启动与验证
    if (nftHoldings.length === 0) {
      message.warning('请先导入钱包地址');
      return;
    }

    if (!isValidAddress(config.nftContractAddress)) {
      message.error('请输入有效的NFT合约地址');
      return;
    }

    if (contractInfo.detectedStandard !== 'ERC721' && contractInfo.detectedStandard !== 'ERC1155') {
      message.error('请先检测NFT合约标准');
      return;
    }

    if (contractInfo.detectedStandard === 'ERC1155' && !config.nftTokenId) {
      message.error('ERC1155标准需要输入Token ID');
      return;
    }

    setIsQuerying(true);
    setQueryProgress({ checked: 0, total: nftHoldings.length });
    setTotalHoldings('0');

    // 重置所有条目状态
    setNftHoldings(prev => prev.map(holding => ({
      ...holding,
      status: 'idle',
      balance: '0'
    })));

    try {
      message.info('开始批量查询NFT持有量任务...');

      // 执行批量查询 (批处理大小设为20)
      await processInBatches(nftHoldings, 20, fetchHoldingForEntry);

      message.success('NFT持有量查询任务完成');
    } catch (error) {
      console.error('批量查询失败:', error);
      message.error('批量查询失败');
    } finally {
      setIsQuerying(false);
    }
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        批量查询NFT工具
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsImportModalVisible(true)}
            style={buttonStyle}
          >
            导入钱包
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteSelected}
            style={buttonStyle}
          >
            删除选中
          </Button>
        </Space>
      </Card>

      {/* NFT持有信息表格 */}
      {nftHoldings.length > 0 && (
        <Card className="spacing-responsive">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              钱包列表 ({nftHoldings.length}个)
            </h3>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                setNftHoldings([]);
                setQueryProgress({ checked: 0, total: 0 });
                setTotalHoldings('0');
              }}
              style={buttonStyle}
            >
              清空全部
            </Button>
          </div>

          <Table
            columns={[
              {
                title: '序号',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '钱包地址',
                dataIndex: 'ownerAddress',
                width: 300,
                render: (text: string) => (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {text.slice(0, 6)}...{text.slice(-4)}
                    </span>
                    <Tooltip title="复制地址">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(text, '地址')}
                      />
                    </Tooltip>
                    <Tooltip title="在区块链浏览器中查看">
                      <Button
                        type="text"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => window.open(`${currentExplorer}${text}`, '_blank')}
                      />
                    </Tooltip>
                  </Space>
                ),
              },
              {
                title: 'NFT持有数量',
                dataIndex: 'balance',
                width: 150,
                render: (text: string, record: NFTHoldingEntry) => {
                  if (record.status === 'loading') {
                    return <Spin size="small" />;
                  }

                  if (record.status === 'error') {
                    return <Tag color="error">查询失败</Tag>;
                  }

                  if (record.status === 'success') {
                    const balance = parseInt(text);
                    if (balance === 0) {
                      return <Tag color="default">0</Tag>;
                    } else {
                      return <Tag color="success">{balance.toLocaleString()}</Tag>;
                    }
                  }

                  return <Tag color="default">待查询</Tag>;
                },
              },
              {
                title: '操作',
                width: 80,
                render: (_, record: NFTHoldingEntry) => (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setNftHoldings(prev => prev.filter(holding => holding.id !== record.id));
                    }}
                  />
                ),
              },
            ]}
            dataSource={nftHoldings}
            rowKey="id"
            rowSelection={{
              selectedRowKeys: nftHoldings.filter(h => h.isSelected).map(h => h.id),
              onChange: (selectedRowKeys) => {
                setNftHoldings(prev => prev.map(holding => ({
                  ...holding,
                  isSelected: selectedRowKeys.includes(holding.id)
                })));
              },
              onSelectAll: (selected) => {
                setNftHoldings(prev => prev.map(holding => ({
                  ...holding,
                  isSelected: selected
                })));
              },
              onSelect: (record, selected) => {
                setNftHoldings(prev => prev.map(holding =>
                  holding.id === record.id ? { ...holding, isSelected: selected } : holding
                ));
              },
            }}
            pagination={false}
            scroll={{ x: 800, y: 400 }}
            size="small"
          />

          {/* 表格底部汇总 */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <Row gutter={16}>
              <Col span={8}>
                <strong>总选中: {nftHoldings.filter(h => h.isSelected).length} 个</strong>
              </Col>
              <Col span={8}>
                <strong>查询进度: {queryProgress.checked} / {queryProgress.total}</strong>
              </Col>
              <Col span={8}>
                <strong>总持有量: {totalHoldings} NFT</strong>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* 底部配置与操作区域 */}
      <Card className="spacing-responsive">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500' }}>
          查询配置
        </h3>

        {/* 选择链 / 节点 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h4>
              <Select
                value={config.rpcUrl}
                onChange={(value) => {
                  setConfig(prev => ({ ...prev, rpcUrl: value }));
                }}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="选择网络"
              >
                {NETWORK_CONFIGS.map(network => (
                  <Select.Option key={network.rpcUrl} value={network.rpcUrl}>
                    {network.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                自定义RPC节点
              </h4>
              <Input
                value={config.rpcUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, rpcUrl: e.target.value }))}
                style={componentStyle}
                placeholder="输入自定义RPC URL"
              />
            </div>
          </Col>
        </Row>

        {/* NFT合约地址和标准检测 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                NFT合约地址 *
              </h4>
              <Input
                value={config.nftContractAddress}
                onChange={(e) => setConfig(prev => ({ ...prev, nftContractAddress: e.target.value }))}
                onBlur={handleDetectStandard}
                style={componentStyle}
                placeholder="输入NFT合约地址"
              />
            </div>
          </Col>

          <Col xs={24} sm={12}>
            <div>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                NFT标准
              </h4>
              <div style={{ padding: '6px 0' }}>
                {contractInfo.detectedStandard === 'Idle' && (
                  <span style={{ color: '#999', fontSize: '14px' }}>请输入合约地址进行检测</span>
                )}
                {contractInfo.detectedStandard === 'Checking' && (
                  <Tag color="blue">检测中...</Tag>
                )}
                {contractInfo.detectedStandard === 'ERC721' && (
                  <Tag color="green">ERC721</Tag>
                )}
                {contractInfo.detectedStandard === 'ERC1155' && (
                  <Tag color="purple">ERC1155</Tag>
                )}
                {contractInfo.detectedStandard === 'Unknown' && (
                  <Tag color="red">未知标准</Tag>
                )}
                {contractInfo.name && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                    {contractInfo.name} ({contractInfo.symbol})
                  </span>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Token ID (仅ERC1155显示) */}
        {contractInfo.detectedStandard === 'ERC1155' && (
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={12}>
              <div>
                <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Token ID *
                </h4>
                <InputNumber
                  value={parseInt(config.nftTokenId)}
                  onChange={(value) => setConfig(prev => ({ ...prev, nftTokenId: (value || 0).toString() }))}
                  min={0}
                  style={{ ...componentStyle, width: '100%' }}
                  placeholder="输入Token ID"
                />
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ paddingTop: '24px' }}>
                <Alert
                  message="ERC1155 Token ID"
                  description="ERC1155标准需要指定Token ID来查询特定NFT的持有量"
                  type="info"
                  showIcon
                />
              </div>
            </Col>
          </Row>
        )}

        {/* 汇总与执行 */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={16}>
            <div style={{
              padding: '12px',
              background: '#fff7e6',
              borderRadius: '6px',
              border: '1px solid #ffd591'
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  🎨 查询统计
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  总地址: {nftHoldings.length} 个 |
                  已查询: {queryProgress.checked} 个 |
                  剩余: {queryProgress.total - queryProgress.checked} 个
                </div>
                <div style={{ fontSize: '13px', color: '#666' }}>
                  总计: <strong style={{ color: '#fa8c16' }}>{totalHoldings} NFT</strong>
                </div>
                {contractInfo.name && (
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    查询合约: <strong>{contractInfo.name} ({contractInfo.symbol})</strong>
                  </div>
                )}
              </Space>
            </div>
          </Col>

          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleQueryHoldings}
                disabled={
                  nftHoldings.length === 0 ||
                  !config.nftContractAddress ||
                  contractInfo.detectedStandard === 'Idle' ||
                  contractInfo.detectedStandard === 'Checking' ||
                  contractInfo.detectedStandard === 'Unknown' ||
                  (contractInfo.detectedStandard === 'ERC1155' && !config.nftTokenId) ||
                  isQuerying
                }
                loading={isQuerying}
                style={{
                  ...primaryButtonStyle,
                  minWidth: '160px',
                  height: '44px',
                  fontSize: '16px'
                }}
              >
                {isQuerying ? '查询中...' : '查询持有量'}
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 导入钱包地址模态框 */}
      <Modal
        title="导入钱包地址"
        open={isImportModalVisible}
        onOk={handleImportWallets}
        onCancel={() => {
          setIsImportModalVisible(false);
          setAddressesInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="批量导入钱包地址"
          description="请粘贴钱包地址，每行一个。系统会查询这些地址在指定NFT合约中的持有情况。"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="钱包地址列表 (每行一个地址)"
            required
          >
            <Input.TextArea
              value={addressesInput}
              onChange={(e) => setAddressesInput(e.target.value)}
              placeholder="请粘贴钱包地址，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={10}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 使用提示 */}
      <Card size="small" style={{
        background: '#f6ffed',
        border: '1px solid #b7eb8f',
        marginTop: '20px'
      }}>
        <div style={{ color: '#52c41a' }}>
          <strong>💡 使用提示：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>支持ERC721和ERC1155两种主流NFT标准的自动检测</li>
            <li>输入NFT合约地址后会自动检测标准类型</li>
            <li>ERC1155标准需要额外指定Token ID（默认为0）</li>
            <li>可以批量查询多个钱包地址的NFT持有情况</li>
            <li>查询结果会显示每个地址的具体持有数量</li>
          </ul>
        </div>
      </Card>

      {/* NFT标准说明 */}
      <Card size="small" style={{
        background: '#fff7e6',
        border: '1px solid #ffd591',
        marginTop: '12px'
      }}>
        <div style={{ color: '#fa8c16' }}>
          <strong>📚 NFT标准说明：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li><strong>ERC721</strong>: 每个Token都是唯一的，查询返回地址拥有的Token总数</li>
            <li><strong>ERC1155</strong>: 支持同质化和非同质化Token，需要指定Token ID查询特定Token的数量</li>
            <li>系统会自动检测合约标准，无需手动选择</li>
            <li>检测失败可能是因为合约不符合标准或网络连接问题</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default NftChecker;
